

import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce, useMediatedState } from 'react-use';
import _clone from 'lodash.clone';
import uuidv4 from 'uuid/v4';
import isNil from 'lodash.isnil';



export const DEBOUNCE_TIME_DEFAULT = 400;
const LOADING_COUNT_INITIAL = 0;
const LOADING_COUNT_FIRST = LOADING_COUNT_INITIAL + 1;

export function useAsync(
{
  fn,
  debounceTime: optionDebounceTime,
  shouldLoadInitially: optionShouldLoadInitially,
  isLoadOnlyByFn,
  onBeforeSetData
},
deps = [])
{
  const debounceTime = optionDebounceTime || DEBOUNCE_TIME_DEFAULT;
  const shouldLoadInitially = isNil(optionShouldLoadInitially) ? true : optionShouldLoadInitially;

  const refOperationId = useRef();
  const canLoadManually = useRef(!isLoadOnlyByFn);
  const loadingCount = useRef(LOADING_COUNT_INITIAL);

  const [data, setData] = useMediatedState(_clone);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(!isLoadOnlyByFn && shouldLoadInitially);
  const [triggerLoad, setTriggerLoad] = useMediatedState(uuidv4);

  const depsForLoad = useMemo(
    () => isLoadOnlyByFn ? [triggerLoad] : [triggerLoad, ...deps],
    [triggerLoad, isLoadOnlyByFn, ...deps]
  );

  useDebounce(() => {
    const loadData = async () => {
      const newOperationId = uuidv4();
      refOperationId.current = newOperationId;

      setError();
      setIsLoading(true);

      let newError;
      let newData;

      try {
        newData = await fn();
      } catch (e) {
        newError = e;
      }

      if (newOperationId === refOperationId.current) {
        if (newError) {
          setError(newError);
        } else {
          if (onBeforeSetData) {
            newData = await onBeforeSetData(newData);
          }

          setData(newData);
        }

        setIsLoading(false);
      }
    };

    loadingCount.current += 1;

    if (loadingCount.current === LOADING_COUNT_FIRST && !shouldLoadInitially) {
      return;
    }

    if (!canLoadManually.current) {
      return;
    }

    loadData();
  }, debounceTime, depsForLoad);

  const load = useCallback(() => {
    canLoadManually.current = true;
    setTriggerLoad();
  }, []);

  return useMemo(() => ({
    data,
    error,
    isLoading: Boolean(isLoading),
    load
  }), [
  data,
  error,
  isLoading,
  load]
  );
}